import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as UsersIcon,
  GridOn as BoardsIcon,
  Payments as PaymentsIcon,
  Receipt as TransactionsIcon,
  Settings as SettingsIcon,
  Inventory as ProductsIcon,
  LocalOffer as CouponsIcon,
  Autorenew as SubscriptionsIcon,
  ExpandLess,
  ExpandMore,
  History as AuditIcon,
  Share as ReferralIcon,
  EmojiEvents as TierIcon,
  Email as EmailIcon,
  Description as TemplatesIcon,
  FormatListBulleted as LogsIcon,
  Webhook as WebhookIcon,
  AdminPanelSettings as AdminIcon,
  Palette as ThemesIcon,
  Toll as TierPricingIcon,
  Notifications as NotificationsIcon,
  Build as TasksIcon,
  SportsFootball as SportsIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 260;

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    label: 'Dashboard',
    path: '/admin',
    icon: <DashboardIcon />,
  },
  {
    label: 'Users',
    path: '/admin/users',
    icon: <UsersIcon />,
  },
  {
    label: 'Boards',
    path: '/admin/boards',
    icon: <BoardsIcon />,
  },
  {
    label: 'Payments',
    path: '/admin/payments',
    icon: <PaymentsIcon />,
    children: [
      {
        label: 'Board Payments',
        path: '/admin/payments',
        icon: <PaymentsIcon />,
      },
      {
        label: 'Transactions',
        path: '/admin/transactions',
        icon: <TransactionsIcon />,
      },
      {
        label: 'Subscriptions',
        path: '/admin/subscriptions',
        icon: <SubscriptionsIcon />,
      },
    ],
  },
  {
    label: 'Products',
    path: '/admin/products',
    icon: <ProductsIcon />,
    children: [
      {
        label: 'All Products',
        path: '/admin/products',
        icon: <ProductsIcon />,
      },
      {
        label: 'Themes',
        path: '/admin/themes',
        icon: <ThemesIcon />,
      },
      {
        label: 'Tier Pricing',
        path: '/admin/tier-config',
        icon: <TierPricingIcon />,
      },
      {
        label: 'Coupons',
        path: '/admin/coupons',
        icon: <CouponsIcon />,
      },
    ],
  },
  {
    label: 'Referrals',
    path: '/admin/referrals',
    icon: <ReferralIcon />,
    children: [
      {
        label: 'All Referrals',
        path: '/admin/referrals',
        icon: <ReferralIcon />,
      },
      {
        label: 'Reward Tiers',
        path: '/admin/referral-tiers',
        icon: <TierIcon />,
      },
    ],
  },
  {
    label: 'Email',
    path: '/admin/email/logs',
    icon: <EmailIcon />,
    children: [
      {
        label: 'Email Logs',
        path: '/admin/email/logs',
        icon: <LogsIcon />,
      },
      {
        label: 'Templates',
        path: '/admin/email/templates',
        icon: <TemplatesIcon />,
      },
      {
        label: 'Webhooks',
        path: '/admin/email/webhooks',
        icon: <WebhookIcon />,
      },
    ],
  },
  {
    label: 'Live Sports',
    path: '/admin/sports',
    icon: <SportsIcon />,
  },
  {
    label: 'Notifications',
    path: '/admin/notifications',
    icon: <NotificationsIcon />,
  },
  {
    label: 'Tasks',
    path: '/admin/tasks',
    icon: <TasksIcon />,
  },
  {
    label: 'Audit Log',
    path: '/admin/audit',
    icon: <AuditIcon />,
  },
  {
    label: 'Settings',
    path: '/admin/settings',
    icon: <SettingsIcon />,
  },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Payments', 'Products', 'Referrals', 'Email']);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      setExpandedMenus((prev) =>
        prev.includes(item.label)
          ? prev.filter((l) => l !== item.label)
          : [...prev, item.label]
      );
    } else {
      navigate(item.path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <AdminIcon color="primary" />
        <Typography variant="h6" fontWeight="bold" color="primary">
          Admin Panel
        </Typography>
      </Box>

      <List sx={{ flex: 1, py: 1 }}>
        {menuItems.map((item) => (
          <Box key={item.label}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleMenuClick(item)}
                selected={!item.children && isActive(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: !item.children && isActive(item.path) ? 'inherit' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
                {item.children && (
                  expandedMenus.includes(item.label) ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>

            {item.children && (
              <Collapse in={expandedMenus.includes(item.label)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.path} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(child.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                        selected={isActive(child.path)}
                        sx={{
                          pl: 4,
                          mx: 1,
                          borderRadius: 1,
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            '&:hover': {
                              bgcolor: 'primary.main',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          SquareUp Admin v1.0
        </Typography>
      </Box>
    </Box>
  );

  // Get current page label for mobile header
  const getCurrentPageLabel = () => {
    // Check children first for more specific match
    for (const item of menuItems) {
      if (item.children) {
        const childMatch = item.children.find(child => isActive(child.path));
        if (childMatch) return childMatch.label;
      }
      if (!item.children && isActive(item.path)) return item.label;
    }
    return 'Admin';
  };

  // Render mobile menu items with collapsible submenus
  const renderMobileMenuItem = (item: MenuItem, isChild = false) => (
    <ListItem key={item.path} disablePadding>
      <ListItemButton
        onClick={() => {
          if (item.children) {
            setExpandedMenus((prev) =>
              prev.includes(item.label)
                ? prev.filter((l) => l !== item.label)
                : [...prev, item.label]
            );
          } else {
            navigate(item.path);
            setMobileOpen(false);
          }
        }}
        selected={!item.children && isActive(item.path)}
        sx={{
          py: 1,
          pl: isChild ? 4 : 2,
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '& .MuiListItemIcon-root': {
              color: 'white',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: !item.children && isActive(item.path) ? 'inherit' : 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{ fontSize: isChild ? '0.875rem' : 'inherit' }}
        />
        {item.children && (
          expandedMenus.includes(item.label) ? <ExpandLess /> : <ExpandMore />
        )}
      </ListItemButton>
    </ListItem>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 'calc(100vh - 64px)' }}>
      {/* Mobile collapsible menu at top */}
      {isMobile && (
        <Paper
          elevation={1}
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <ListItemButton
            onClick={handleDrawerToggle}
            sx={{
              py: 1.5,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AdminIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="medium">
                {getCurrentPageLabel()}
              </Typography>
            </Box>
            {mobileOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={mobileOpen} timeout="auto" unmountOnExit>
            <List sx={{ py: 0, bgcolor: 'grey.50', maxHeight: '60vh', overflow: 'auto' }}>
              {menuItems.map((item) => (
                <Box key={item.label}>
                  {renderMobileMenuItem(item)}
                  {item.children && (
                    <Collapse in={expandedMenus.includes(item.label)} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ bgcolor: 'grey.100' }}>
                        {item.children.map((child) => renderMobileMenuItem(child, true))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              ))}
            </List>
          </Collapse>
        </Paper>
      )}

      {/* Desktop Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 }, display: { xs: 'none', md: 'block' } }}
      >
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              top: 64,
              height: 'calc(100% - 64px)',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100%',
          bgcolor: 'grey.50',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
